// Kauflands Sicht auf das PRODUKT — neben der Sicht auf das Angebot.
//
// ANLASS (2026-08-22, Betreiber): „Kaufland meldet manchmal gelistet, ohne Bestand … in
// Wahrheit gibt es aber noch Fehler im Produkt."
//
// Es sind zwei Quellen mit zwei verschiedenen Aussagen:
//   - Der Angebots-Spiegel (`kaufland.units`) speist die Pille „Gelistet, ohne Bestand". Er
//     sagt, ob ein Angebot existiert und Bestand hat — ueber Produktmaengel weiss er nichts.
//   - `GET /v2/product-data/status/{ean}` sagt, ob Kaufland den Katalogeintrag als bereit
//     fuehrt und welche Attribute fehlen. Bis zum 2026-08-22 wurde das nach der Freigabe nie
//     wieder abgefragt.
//
// Der Hinweis steht deshalb NEBEN der Pille, nicht statt ihr. Er widerspricht ihr nicht — er
// ergaenzt die zweite Aussage, die vorher gefehlt hat.
//
// Bewusst DB- und DOM-frei, damit die Regeln testbar sind (Muster: `status-watch.ts`).

import { statusAlter } from './status-watch.js';

/** Der Text zum Ausgang einer Produktstand-Abfrage.
 *
 *  Eigene Funktion, weil der Erfolgs-Toast sonst auch bei einem Fehlschlag erschiene. Genau
 *  das faellt im Ausrollfenster auf: Zwischen Frontend-Deploy und Migration 0057 gibt es die
 *  RPC noch nicht, die Abfrage schlaegt fehl -- und ein „wird gefragt" waere dann gelogen.
 *
 *  Pure -- keine IO. */
export function abfrageMeldung(fehler: string | null): { text: string; art: 'info' | 'error' } {
  if (fehler === null) return { text: 'Kaufland wird in den nächsten Sekunden gefragt.', art: 'info' };
  return { text: `Produktstand konnte nicht abgefragt werden: ${fehler}`, art: 'error' };
}

/** Wie lange ein geholter Produktstand als frisch gilt.
 *  Deckt sich mit der Frische-Schwelle in `kaufland_enqueue_product_status_check`
 *  (Migration 0057) — laufen die beiden auseinander, fragt die Oberflaeche ins Leere. */
export const FRISCHE_MS = 2 * 60_000;

/** Zustaende, in denen ueberhaupt etwas eingereicht wurde. Vorher gibt es bei Kaufland
 *  nichts zu erfragen, und ein 404 saehe aus wie ein Befund. */
const EINGEREICHT = new Set(['published', 'error', 'archived']);

export interface ProduktstandEingabe {
  status: string;
  /** `kaufland.listings.kaufland_status` — SUCCESS | FAIL | PENDING | null. */
  kauflandStatus: string | null;
  /** Kauflands Begruendung, unveraendert. */
  reason: string | null;
  /** `kaufland_status_polled_at` in ms, `null` wenn nie abgefragt. */
  polledAtMs: number | null;
  jetztMs: number;
}

export interface ProduktstandHinweis {
  ton: 'ok' | 'warnung' | 'fehler' | 'neutral';
  text: string;
}

/**
 * Der Hinweis zum Produktstand, oder `null` wenn keiner hingehoert.
 *
 * Schweigt waehrend `validating`: Dort steht bereits das Wartebanner mit demselben Grund,
 * zwei Meldungen nebeneinander waeren nur Laerm.
 *
 * Pure — keine IO.
 */
export function produktstandHinweis(e: ProduktstandEingabe): ProduktstandHinweis | null {
  if (!EINGEREICHT.has(e.status)) return null;

  const alter = e.polledAtMs === null ? null : statusAlter(e.jetztMs - e.polledAtMs);
  const stand = alter ? ` (Stand ${alter})` : '';

  if (e.kauflandStatus === null) {
    return { ton: 'neutral', text: 'Produktstand bei Kaufland noch nicht abgefragt.' };
  }
  if (e.kauflandStatus === 'SUCCESS') {
    return { ton: 'ok', text: `Kaufland führt das Produkt als bereit${stand}.` };
  }
  if (e.kauflandStatus === 'FAIL') {
    return {
      ton: 'fehler',
      text: `Kaufland hat die Produktdaten abgelehnt${stand}${e.reason ? `: ${e.reason}` : '.'}`,
    };
  }
  // PENDING und alles Unbekannte. Das ist der gemeldete Fall: Das Angebot steht, das Produkt
  // ist aber nicht bereit -- und ohne diesen Hinweis sieht man nur die Pille.
  return {
    ton: 'warnung',
    text: `Kaufland führt das Produkt NICHT als bereit${stand}${e.reason ? `: ${e.reason}` : '.'}`,
  };
}

/**
 * Beim Oeffnen automatisch nachfragen? (Wunsch des Betreibers, 2026-08-22.)
 *
 * Mit Frische-Schwelle, sonst kostet jedes Aufklappen einen Kaufland-Aufruf — bei einem
 * Varianten-Reiter mit acht Kindern acht auf einmal. Waehrend `validating` haelt sich diese
 * Abfrage raus: dort laeuft bereits der eigene Poll-Takt der Produktanlage.
 *
 * Pure — keine IO.
 */
export function darfAutomatischAbfragen(
  e: { status: string; polledAtMs: number | null; jetztMs: number },
): boolean {
  if (!EINGEREICHT.has(e.status)) return false;
  if (e.polledAtMs === null) return true;
  return e.jetztMs - e.polledAtMs > FRISCHE_MS;
}
